import { useState } from 'react'
import TextInput from '../TextInput/TextInput'
import RadioInput from '../RadioInput/RadioInput'
import TextInputArea from '../TextInputArea/TextInputArea'
import FormSuccessDialog from '../FormSuccessDialog/FormSuccessDialog'
import FormErrorDialog from '../FormErrorDialog/FormErrorDialog'
import useReportError from '../../hooks/useReportError'
import CircularProgress from '@mui/material/CircularProgress'
import { BASE_URL } from '../../constants'
import { getRecaptchaV3Token } from '../../utils/recaptcha'
import RecaptchaModal from '../Recaptcha/RecaptchaModal'

export const ContactFormContactPage = props => {
  const { updateShowSuccess, updateShowError } = props
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [userType, setUserType] = useState('')
  const [message, setMessage] = useState('')
  const [messageError, setMessageError] = useState('')
  const { reportError } = useReportError()
  const [stateFocus, setStateFocus] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [disabledField, setDisabledField] = useState(false)
  const [showCheckboxFallback, setShowCheckboxFallback] = useState(false)
  const [pendingSubmitData, setPendingSubmitData] = useState(null)

  const FormProcess = async e => {
    e.preventDefault()

    if (!message.trim()) {
      setMessageError('Message is required')
      return
    }

    setMessageError('')
    setDisabledField(true)
    setIsSubmitting(true)

    let token
    try {
    token = await getRecaptchaV3Token('contact_page')
    } catch (err) {
    console.error('[recaptcha] v3 token generation failed', err)
      updateShowError(true)
      setDisabledField(false)
      setIsSubmitting(false)
      return
    }

    const payload = {
      first_name: firstName,
      last_name: lastName,
      email_address: email,
      who_are_you: userType,
      city: city,
      state: state,
      phone_number: phoneNumber,
      message: message,
      captcha_token: {
        token,
        version: 'v3',
        provider: 1,
      },
    }

    const doSubmit = async body => {
  // normal submit
      return fetch(`${BASE_URL}/Marketing/ContactUs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
    }

    let response = null
    try {
      response = await doSubmit(payload)
    } catch (error) {
      await reportError(error)
    }

    if (response && response.status === 400) {
      // server requests v2 checkbox fallback
      setPendingSubmitData(payload)
      setShowCheckboxFallback(true)
      setDisabledField(false)
      setIsSubmitting(false)
      return
    }

    if (response && response?.status) {
      if (response.status < 200 || response.status > 299) {
        updateShowError(true)
        await reportError(response)
      } else {
        updateShowSuccess(true)
      }
    }
    setDisabledField(false)
    setIsSubmitting(false)
  }

  const handleCheckboxVerify = async token => {
  // v2 token received
    if (!pendingSubmitData) return
    setShowCheckboxFallback(false)
    setDisabledField(true)
    setIsSubmitting(true)
    const response = await fetch(`${BASE_URL}/Marketing/ContactUs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...pendingSubmitData,
        captcha_token: { token, version: 'v2', provider: 1 },
      }),
    })
    setPendingSubmitData(null)
    if (response.status >= 200 && response.status <= 299) {
      updateShowSuccess(true)
    } else {
      updateShowError(true)
    }
    setDisabledField(false)
    setIsSubmitting(false)
  }

  const handleDelayedBlur = () => {
    setTimeout(() => {
      setStateFocus(false)
    }, 100)
  }



  return (
    <div className="max-w-screen-xl mx-auto ">
      <div id="recaptcha-container-invisible" style={{ display: 'none' }}></div>
      <div className="relative">
        <form
          onSubmit={FormProcess}
          className="grid grid-cols-1 md:grid-cols-2 text-grey"
        >
          <div className="col-span-1">
            <TextInput
              required
              type="text"
              minLength={2}
              maxLength={20}
              disabled={disabledField}
              value={firstName}
              change={e => {
                setFirstName(e.target.value)
              }}
            >
              First Name
            </TextInput>
          </div>
          <div className="col-span-1">
            <TextInput
              required
              type="text"
              minLength={2}
              maxLength={20}
              disabled={disabledField}
              value={lastName}
              change={e => {
                setLastName(e.target.value)
              }}
            >
              Last Name
            </TextInput>
          </div>

          <div className="col-span-1 md:col-span-2">
            <TextInput
              required
              type="email"
              minLength={2}
              maxLength={60}
              disabled={disabledField}
              value={email}
              change={e => {
                setEmail(e.target.value)
              }}
            >
              Email
            </TextInput>
          </div>
          <div className="col-span-1 md:col-span-2">
            <TextInput
              required
              type="tel"
              minLength={2}
              maxLength={20}
              disabled={disabledField}
              value={phoneNumber}
              change={e => {
                setPhoneNumber(e.target.value)
              }}
            >
              Phone Number
            </TextInput>
          </div>
          <div className="col-span-1 md:col-span-1">
            <TextInput
              required
              minLength={2}
              maxLength={20}
              disabled={disabledField}
              value={city}
              change={e => {
                setCity(e.target.value)
              }}
            >
              City
            </TextInput>
            <div className="relative pt-4">
              <TextInput
                required
                minLength={2}
                maxLength={20}
                disabled={disabledField}
                value={state}
                change={e => {
                  setState(e.target.value)
                }}
                onBlurCustom={() => handleDelayedBlur()}
                onFocusCustom={() => setStateFocus(true)}
              >
                State
              </TextInput>
              {stateFocus && (
                <div className="absolute top-[105%] right-0 flex flex-col gap-2 h-[400px] w-[95%] overflow-y-scroll bg-white-light z-10 shadow-md py-2">
                  {STATES.map((state, index) => {
                    return (
                      <span
                        key={index}
                        value={state}
                        className="p-2 px-4 cursor-pointer hover:bg-blue-light/20"
                        onClick={() => setState(state)}
                      >
                        {state}
                      </span>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="col-span-1 md:col-span-1">
            <div
              className="border-1 border-solid border-white mx-1.5 my-2 md:m-3.5 pb-4 bg-white-light"
              role="group"
            >
              <h4 className="mx-5 my-5 text-xl text-darkGray">
                Who are you? *
              </h4>
              <RadioInput
                required
                name="userType"
                value="employer"
                selectedValue={userType}
                change={e => {
                  setUserType(e.target.value)
                }}
              >
                Employer
              </RadioInput>
              <RadioInput
                required
                name="userType"
                value="individual"
                selectedValue={userType}
                change={e => {
                  setUserType(e.target.value)
                }}
              >
                Individual
              </RadioInput>
              <RadioInput
                required
                name="userType"
                value="broker"
                selectedValue={userType}
                change={e => {
                  setUserType(e.target.value)
                }}
              >
                Broker
              </RadioInput>
            </div>
          </div>

          <div className="col-span-1 md:col-span-2">
            <TextInputArea
              required
              disabled={disabledField}
              minLength={10}
              maxLength={2000}
              value={message}
              error={messageError}
              change={e => {
                setMessage(e.target.value)
                if (messageError) setMessageError('')
              }}
            >
              Message
            </TextInputArea>
          </div>
          <div className="col-span-1 mx-auto mt-8 md:col-span-2">
            <button
              type="submit"
              className="flex items-center gap-2 px-12 py-2 text-2xl bg-blue text-white-light"
            >
              {isSubmitting && <CircularProgress size="1.5rem" />}
              Send
            </button>
          </div>
        </form>
        {showCheckboxFallback && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: 'rgba(0,0,0,0.5)', position: 'absolute', inset: 0 }} />
            <div style={{ zIndex: 10000, background: '#fff', padding: 20, borderRadius: 8 }}>
              <p style={{ marginBottom: 10 }}>Please complete the verification to continue</p>
              <RecaptchaModal
                onVerify={token => handleCheckboxVerify(token)}
                onError={() => {
                  setShowCheckboxFallback(false)
                  updateShowError(true)
                }}
                onClose={() => setShowCheckboxFallback(false)}
              />
              <div style={{ marginTop: 8, textAlign: 'right' }}>
                <button type="button" className="px-4 py-2 text-sm" onClick={() => { setShowCheckboxFallback(false) }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
]

export default ContactFormContactPage
